import React, { useMemo } from 'react';

import { Timeline, Empty, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatDateTime } from '@utils/dateTime';

import type { ProductSerialNumberTimelineEvent } from '@services/shared/productSerialNumberSchemas';

// ----------------------------------------------------------------------------
// Timeline events shape (對齊 ProductSerialNumberTimelineBuilder + Zod schema)
//
//   - receipt:     ref = { type: 'inbound',         id, code }
//   - fulfillment: ref = { type: 'fulfillment',     id, code, order_id, order_code, parcel_id? }
//   - return:      ref = { type: 'return_receipt',  id, code }
//   - disposition: ref = null;  disposition = 'restock' | 'scrap'
//
// 路由策略：route paths 是 root-relative，不帶 /admin or /operator 前綴
// (見 app/javascript/components/{admin,operator}/menuConfig)。
//   - inbound        → /inbounds/:id           (admin + operator 皆有)
//   - return_receipt → /return_receipts/:id    (admin + operator 皆有)
//   - fulfillment:   admin 沒有 standalone /fulfillments/:id route，所以 admin 用
//                    `/orders/:order_id` 跳到對應訂單; operator 走 /fulfillments/:id
//   - disposition:   ref = null，沒有可導向頁
// ----------------------------------------------------------------------------

interface Props {
  role: 'admin' | 'operator';
  events: ProductSerialNumberTimelineEvent[];
}

type TimelineRef = NonNullable<ProductSerialNumberTimelineEvent['ref']>;

const refToPath = (
  role: 'admin' | 'operator',
  ref: TimelineRef,
): string | null => {
  if (ref.type === 'inbound') {
    return ref.id ? `/inbounds/${ref.id}` : null;
  }
  if (ref.type === 'return_receipt') {
    return ref.id ? `/return_receipts/${ref.id}` : null;
  }
  if (ref.type === 'fulfillment') {
    if (role === 'operator') {
      return ref.id ? `/fulfillments/${ref.id}` : null;
    }
    // admin: jump to the order page instead (no standalone fulfillment detail)
    return ref.order_id ? `/orders/${ref.order_id}` : null;
  }
  return null;
};

// 事件 type → i18n key + Tag color (參考 POTimeline / OrderTimeline 的 visual cue)
const EVENT_COLOR_BY_TYPE: Record<ProductSerialNumberTimelineEvent['type'], string> = {
  receipt: 'green',
  fulfillment: 'blue',
  return: 'orange',
  disposition: 'red',
};

const eventLabelKey = (event: ProductSerialNumberTimelineEvent): string => {
  if (event.type === 'disposition') {
    return event.disposition === 'restock'
      ? 'shared.product_serial_numbers.timeline.events.disposition_restock'
      : 'shared.product_serial_numbers.timeline.events.disposition_scrap';
  }
  return `shared.product_serial_numbers.timeline.events.${event.type}`;
};

const ProductSerialNumberTimeline: React.FC<Props> = ({ role, events }) => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    return events.map((event, idx) => {
      const ref = event.ref;
      const path = ref ? refToPath(role, ref) : null;
      const code = ref?.code ?? '';
      // 對 fulfillment event 額外帶 order_code 提示 (PSN 出貨關聯的訂單代碼)
      const orderCode =
        event.type === 'fulfillment' && ref?.order_code ? ref.order_code : '';

      return {
        key: idx,
        color: EVENT_COLOR_BY_TYPE[event.type],
        children: (
          <div
            data-testid={`psn-timeline-event-${event.type}-${idx}`}
            style={{ fontSize: 13 }}
          >
            <div style={{ marginBottom: 2 }}>
              <Tag color={EVENT_COLOR_BY_TYPE[event.type]}>
                {t(eventLabelKey(event))}
              </Tag>
              {path && code ? (
                <Link to={path} data-testid={`psn-timeline-ref-${event.type}-${idx}`}>
                  {code}
                </Link>
              ) : (
                code && <span>{code}</span>
              )}
              {orderCode && (
                <span style={{ color: '#999', marginLeft: 8 }}>/ {orderCode}</span>
              )}
            </div>
            <div style={{ color: '#999', fontSize: 11 }}>
              {formatDateTime(event.event_at)}
            </div>
          </div>
        ),
      };
    });
  }, [events, role, t]);

  if (events.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('shared.product_serial_numbers.drawer.empty_timeline')}
      />
    );
  }

  return <Timeline items={items} />;
};

export default ProductSerialNumberTimeline;
